import React from 'react';
import type { AddEthereumChainParameter } from 'viem';

import type { WalletProvider } from 'types/web3';

import config from 'configs/app';
import { useMultichainContext } from 'lib/contexts/multichain';
import { SECOND } from 'toolkit/utils/consts';

import useRewardsActivity from '../hooks/useRewardsActivity';
import useProvider from './useProvider';
import { getHexadecimalChainId } from './utils';

function getParams(chainConfig: typeof config): AddEthereumChainParameter {
  if (!chainConfig.chain.id) {
    throw new Error('Missing required chain config');
  }

  return {
    chainId: getHexadecimalChainId(Number(chainConfig.chain.id)),
    chainName: chainConfig.chain.name ?? '',
    nativeCurrency: {
      name: chainConfig.chain.currency.name ?? '',
      symbol: chainConfig.chain.currency.symbol ?? '',
      decimals: chainConfig.chain.currency.decimals ?? 18,
    },
    rpcUrls: chainConfig.chain.rpcUrls,
    blockExplorerUrls: [ chainConfig.app.baseUrl ],
  };
}

export default function useAddChain() {
  const { data: { wallet, provider } = {} } = useProvider();
  const { trackUsage } = useRewardsActivity();
  const multichainContext = useMultichainContext();

  const chainConfig = multichainContext?.chain.app_config || config;

  return React.useCallback(async(targetProvider?: WalletProvider) => {
    const currentProvider = targetProvider || provider;
    if (!wallet || !currentProvider) {
      throw new Error('Wallet or provider not found');
    }

    const start = Date.now();

    try {
      await currentProvider.request({
        method: 'wallet_addEthereumChain',
        params: [ getParams(chainConfig) ],
      });

      // If network is already added, the promise resolves immediately
      if (Date.now() - start > SECOND) {
        await trackUsage('add_network');
      }
    } catch (error) {
      // Handle specific error cases
      if ((error as any).code === 4902) {
        // Chain not added yet - this is expected
        throw error;
      } else if ((error as any).code === -32002) {
        // Request already pending
        throw new Error('Please check your wallet - a request is already pending');
      } else {
        throw error;
      }
    }
  }, [ wallet, provider, chainConfig, trackUsage ]);
}
